/**
 * WHAT THE GAUGES SAID, ON AN AIR CONDITIONING JOB.
 *
 * Gap 5 of five from the 2026-09-16 compliance audit: an A/C invoice
 * needs the high and low side pressures and the center outlet
 * temperature, and the shop sells A/C work with nowhere to put them.
 *
 * ─── ZERO IS A READING. THIS IS THE WHOLE MODULE ───────────────────────
 *
 * A low side of 0 psi is not a missing measurement, it is an empty
 * system, which is a diagnosis and quite often the reason the customer
 * came in. Every function here distinguishes 0 from null and none of them
 * uses a falsy test on a number. `if (!psi)` is the bug this file exists
 * to not have, and it is the same family as `part_condition ?? "new"`.
 *
 * ─── AND A VENT TEMPERATURE CAN BE NEGATIVE ────────────────────────────
 *
 * Not on a car, in practice. But the column allows it, a technician can
 * type a minus sign, and a function that silently treats -5 as absent
 * would drop a reading somebody took. Range checking is the database's
 * job here; this module's job is to not lose anything.
 *
 * ─── AMBIENT IS OPTIONAL AND NOT PART OF "COMPLETE" ────────────────────
 *
 * 250 psi on the high side is healthy at 105F and a fault at 70F, so the
 * ambient reading is what makes the other three readable a year later.
 * It is still not one of the three the audit named, so a job carrying the
 * three named readings counts as complete and the fourth is a bonus that
 * prints when it is there.
 *
 * Pure, no imports, shared with the server -- like partcondition.ts.
 */

export type Readings = {
  /** High side gauge pressure, psi. */
  high: number | null;
  /** Low side gauge pressure, psi. */
  low: number | null;
  /** Center outlet (vent) temperature, Fahrenheit. */
  vent: number | null;
  /** Outside air temperature, Fahrenheit. Optional context. */
  ambient: number | null;
};

/**
 * A database value as a number, or null.
 *
 * PostgREST hands back a numeric as a string, so "0" has to survive and
 * become 0 rather than being swallowed by a truthiness test somewhere up
 * the chain. An empty string, whitespace and a non-number are all null.
 */
export function reading(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Gather the four columns off a job row. */
export function readingsOf(job: Record<string, unknown> | null | undefined): Readings {
  return {
    high: reading(job?.["ac_high_psi"]),
    low: reading(job?.["ac_low_psi"]),
    vent: reading(job?.["ac_vent_f"]),
    ambient: reading(job?.["ac_ambient_f"]),
  };
}

/** Whether anybody has written down anything at all. */
export function anyReading(r: Readings): boolean {
  return r.high !== null || r.low !== null || r.vent !== null || r.ambient !== null;
}

/**
 * Whether the three the audit named are all present.
 *
 * Ambient deliberately excluded -- see the header.
 */
export function readingsComplete(r: Readings): boolean {
  return r.high !== null && r.low !== null && r.vent !== null;
}

/** Which of the three named readings are still missing, in plain words. */
export function missingReadings(r: Readings): string[] {
  const out: string[] = [];
  if (r.high === null) out.push("high side pressure");
  if (r.low === null) out.push("low side pressure");
  if (r.vent === null) out.push("center outlet temperature");
  return out;
}

/**
 * Words that mean "this job involved the air conditioning".
 *
 * Matched against the free text line descriptions, because that is what
 * the book holds. Checked against the 15 A/C lines actually on the book
 * on 2026-09-18: recharges, a compressor, a condenser, a receiver drier,
 * refrigerant by the ounce and by the pound, and a mode door.
 *
 * "ac" on its own is NOT in the list and must not be. It matches "vacuum",
 * "back", "track" and a serpentine belt whose part description happens to
 * read "SERPENTINE TENS ALT IDLER PS AC WP AC" -- that belt is on the
 * book and it is not an air conditioning job.
 */
const AC_WORDS = [
  "a/c",
  "a / c",
  "air cond",
  "aircond",
  "refrigerant",
  "freon",
  "r134",
  "r-134",
  "1234yf",
  "evaporator",
  "condenser",
  "receiver drier",
  "receiver dryer",
  "accumulator",
  "expansion valve",
  "orifice tube",
];

/** Whether one line reads as air conditioning work. */
export function isAcLine(description: unknown): boolean {
  const d = String(description ?? "").toLowerCase();
  if (!d.trim()) return false;
  if (AC_WORDS.some((w) => d.includes(w))) return true;
  // "ac compressor" and "ac recharge" are worth catching, and both are on
  // the book. The space on each side is what keeps "vacuum" out.
  return /\bac\s+(compressor|recharge|charge|system|service|evac|repair|line|hose)\b/.test(d);
}

/** The air conditioning lines on a job, as a list rather than a boolean. */
export function acLines<
  T extends { description?: string | null },
>(lines: readonly T[]): T[] {
  return lines.filter((l) => isAcLine(l.description));
}

/**
 * What the invoice prints, or null.
 *
 * Null when nothing was written down. A partial set still prints what
 * there is: two readings on the paper are better than none, and hiding
 * them until the set is complete would lose real measurements.
 */
export function readingsLine(r: Readings): string | null {
  if (!anyReading(r)) return null;
  const bits: string[] = [];
  if (r.high !== null) bits.push(`high side ${r.high} psi`);
  if (r.low !== null) bits.push(`low side ${r.low} psi`);
  if (r.vent !== null) bits.push(`center outlet ${r.vent}°F`);
  if (r.ambient !== null) bits.push(`ambient ${r.ambient}°F`);
  return bits.join(", ");
}

/**
 * What the invoice screen tells the advisor, or null when all is well.
 *
 * REPORTS, DOES NOT BLOCK, like every other nag in this work except the
 * teardown disclosure. A reading that was never taken cannot be produced
 * by refusing to send the invoice; all that achieves is somebody typing
 * a plausible number, which is worse than a blank.
 */
export function acNag<
  T extends { description?: string | null },
>(lines: readonly T[], r: Readings): string | null {
  if (!acLines(lines).length) return null;
  const missing = missingReadings(r);
  if (!missing.length) return null;
  const list = missing.length === 1
    ? missing[0]
    : `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`;
  return `This job has air conditioning work on it and no ${list} written `
    + `down. Those belong on the invoice. Add them on the job `
    + `screen while somebody can still remember the gauges.`;
}
